import React, { useMemo } from 'react';
import Icon from '../../Icon';
import { C } from '../../../utils/styles';

export default function EarningsStabilityCard({ employees, hide }) {
  const stats = useMemo(() => {
    if (!Array.isArray(employees) || employees.length === 0) return null;
    let stable = 0, highOt = 0, variable = 0, hasEarnings = 0;
    for (const e of employees) {
      if (!e.earnings) continue;
      hasEarnings += 1;
      const reg = e.earnings.regularPct;
      if (reg >= 0.8) stable += 1;
      if (e.earnings.otFlag) highOt += 1;
      if (e.earnings.variableIncomeFlag || (reg > 0 && reg < 0.6)) variable += 1;
    }
    return { stable, highOt, variable, hasEarnings };
  }, [employees]);

  if (hide || !stats || stats.hasEarnings === 0) return null;

  const Tile = ({ icon, color, bg, count, label }) => (
    <div style={{
      flex: 1, minWidth: 200, padding: 16, borderRadius: 10, background: bg,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8, background: '#fff', color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} style={{ fontSize: 20 }} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{count}</div>
        <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div data-testid="smart-upload-stability-panel" style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: 12, marginBottom: 16 }}>
        <Icon name="stats-chart-outline" style={{ fontSize: 20, color: C.blue, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Earnings Stability Analysis</div>
          <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5 }}>
            Based on the earnings code breakdown in your payroll export. These flags improve eligibility recommendations by accounting for real-world pay variability.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile icon="checkmark-circle" color={C.green} bg={C.greenLight} count={stats.stable} label="Stable income (>80% regular pay)" />
        <Tile icon="warning-outline" color={C.orange} bg={C.orangeLight} count={stats.highOt} label="High overtime exposure (>40% from OT)" />
        <Tile icon="alert-circle" color={C.red} bg={'#fee2e2'} count={stats.variable} label="Highly variable income (<60% regular)" />
      </div>

      <div style={{
        background: C.bg, padding: 12, borderRadius: 8, fontSize: 13, color: C.textMid, lineHeight: 1.6,
      }}>
        <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>Recommendations</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li>Stable income employees: full optimization recommended.</li>
          <li>High OT employees: consider lower-tier plans to ensure deductions remain viable in low-OT pay periods.</li>
          <li>Variable income: review case-by-case — annual eligibility may be misleading if pay periods vary dramatically.</li>
        </ul>
      </div>
    </div>
  );
}
